import React from "react";

/*
 * Small, safe Markdown renderer (no dangerouslySetInnerHTML, no dependency).
 * Supports: headings, paragraphs, **bold**, *italic*, `code`, [links](https://..),
 * bullet / numbered lists (nested), GFM tables, ``` code blocks, > quotes, --- rules.
 */

// Single "_" italics are not supported on purpose: field names like company_project_code_bd
// would otherwise turn italic.
const INLINE_SRC = "(\\*\\*[^*]+\\*\\*|`[^`]+`|\\[[^\\]]+\\]\\([^)\\s]+\\)|\\*[^*\\s][^*]*\\*)";

function renderInline(text, keyPrefix = "i") {
  const out = [];
  let last = 0;
  let m;
  let i = 0;
  // a fresh regex per call: renderInline is recursive (bold inside italic etc.), and a shared
  // global regex would have its lastIndex reset by the inner call -> infinite loop
  const re = new RegExp(INLINE_SRC, "g");
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    const key = `${keyPrefix}-${i++}`;
    if (tok.startsWith("**")) {
      out.push(<strong key={key}>{renderInline(tok.slice(2, -2), key)}</strong>);
    } else if (tok.startsWith("`")) {
      out.push(<code key={key} className="bdai-md-code">{tok.slice(1, -1)}</code>);
    } else if (tok.startsWith("[")) {
      const lm = tok.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
      const href = lm && /^https?:\/\//i.test(lm[2]) ? lm[2] : null;
      out.push(
        href ? (
          <a key={key} href={href} target="_blank" rel="noopener noreferrer" className="bdai-md-link">
            {lm[1]}
          </a>
        ) : (
          lm ? lm[1] : tok
        )
      );
    } else {
      out.push(<em key={key}>{renderInline(tok.slice(1, -1), key)}</em>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const isTableSep = (line) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(line);
const splitRow = (line) =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());
const LIST_RE = /^(\s*)([-*•+]|\d+[.)])\s+(.*)$/;

function parseList(lines, start) {
  // returns [element, nextIndex]
  const items = [];
  const baseIndent = lines[start].match(LIST_RE)[1].length;
  const ordered = /\d/.test(lines[start].match(LIST_RE)[2]);
  let i = start;
  while (i < lines.length) {
    const m = lines[i].match(LIST_RE);
    if (!m) {
      // continuation line of previous item
      if (lines[i].trim() && /^\s{2,}/.test(lines[i]) && items.length) {
        items[items.length - 1].text += ` ${lines[i].trim()}`;
        i++;
        continue;
      }
      break;
    }
    const indent = m[1].length;
    if (indent < baseIndent) break;
    if (indent > baseIndent && items.length) {
      const [child, next] = parseList(lines, i);
      items[items.length - 1].children.push(child);
      i = next;
      continue;
    }
    items.push({ text: m[3], children: [] });
    i++;
  }
  const Tag = ordered ? "ol" : "ul";
  const el = (
    <Tag className={`bdai-md-list ${ordered ? "bdai-md-ol" : "bdai-md-ul"}`} key={`l${start}`}>
      {items.map((it, idx) => (
        <li key={idx}>
          {renderInline(it.text, `li${start}-${idx}`)}
          {it.children}
        </li>
      ))}
    </Tag>
  );
  return [el, i];
}

export default function Markdown({ text }) {
  if (!text) return null;
  const lines = String(text).replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  let para = [];

  const flushPara = () => {
    if (para.length) {
      const k = `p${blocks.length}`;
      blocks.push(
        <p key={k} className="bdai-md-p">
          {para.map((l, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <br />}
              {renderInline(l, `${k}-${idx}`)}
            </React.Fragment>
          ))}
        </p>
      );
      para = [];
    }
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      flushPara();
      i++;
      continue;
    }

    // code block
    if (trimmed.startsWith("```")) {
      flushPara();
      const code = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) code.push(lines[i++]);
      i++;
      blocks.push(
        <pre key={`c${i}`} className="bdai-md-pre">
          <code>{code.join("\n")}</code>
        </pre>
      );
      continue;
    }

    // heading
    const h = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      flushPara();
      const level = Math.min(h[1].length + 1, 6); // # -> h2 so it never dominates the panel
      const Tag = `h${level}`;
      blocks.push(
        <Tag key={`h${i}`} className={`bdai-md-h bdai-md-h${h[1].length}`}>
          {renderInline(h[2], `h${i}`)}
        </Tag>
      );
      i++;
      continue;
    }

    // rule
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
      flushPara();
      blocks.push(<hr key={`hr${i}`} className="bdai-md-hr" />);
      i++;
      continue;
    }

    // table
    if (trimmed.includes("|") && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      flushPara();
      const header = splitRow(line);
      const aligns = splitRow(lines[i + 1]).map((c) =>
        c.startsWith(":") && c.endsWith(":") ? "center" : c.endsWith(":") ? "right" : "left"
      );
      const rows = [];
      i += 2;
      while (i < lines.length && lines[i].trim().includes("|") && lines[i].trim()) rows.push(splitRow(lines[i++]));
      blocks.push(
        <div key={`t${i}`} className="bdai-md-table-wrap">
          <table className="bdai-md-table">
            <thead>
              <tr>
                {header.map((c, idx) => (
                  <th key={idx} style={{ textAlign: aligns[idx] || "left" }}>
                    {renderInline(c, `th${i}-${idx}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>
                  {header.map((_, ci) => (
                    <td key={ci} style={{ textAlign: aligns[ci] || "left" }}>
                      {renderInline(r[ci] || "", `td${i}-${ri}-${ci}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // blockquote
    if (trimmed.startsWith(">")) {
      flushPara();
      const quote = [];
      while (i < lines.length && lines[i].trim().startsWith(">")) quote.push(lines[i++].trim().replace(/^>\s?/, ""));
      blocks.push(
        <blockquote key={`q${i}`} className="bdai-md-quote">
          {renderInline(quote.join(" "), `q${i}`)}
        </blockquote>
      );
      continue;
    }

    // list
    if (LIST_RE.test(line)) {
      flushPara();
      const [el, next] = parseList(lines, i);
      blocks.push(el);
      i = next;
      continue;
    }

    para.push(trimmed);
    i++;
  }
  flushPara();
  return <div className="bdai-md">{blocks}</div>;
}
